import { ReactNode, useState } from "react";

import MuiAutocomplete, { AutocompleteProps as MuiAutocompleteProps } from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import FormHelperText from "@mui/material/FormHelperText";

// ============= Shared base props =============

interface NamedOption {
  id?: number | string;
  name: string;
}

const defaultGetOptionLabel = (option: NamedOption | string): string => {
  if (typeof option === "string") return option;
  if (!option?.name) return "";
  return option.id != null ? `${option.name} (${option.id})` : option.name;
};

interface AutocompleteBaseProps<T> {
  label: string;
  options: T[];
  onBlur?: () => void;
  getOptionLabel?: (option: T) => string;
  isOptionEqualToValue?: (option: T, value: T) => boolean;
  groupBy?: (option: T) => string;
  error?: string;
  disabled?: boolean;
  placeholder?: string;
}

// ============= Single =============

export interface AutocompleteProps<T> extends AutocompleteBaseProps<T> {
  value: T | null;
  onChange: (value: T | null) => void;
  renderOption?: MuiAutocompleteProps<T, false, false, false>["renderOption"];
  filterOptions?: MuiAutocompleteProps<T, false, false, false>["filterOptions"];
}

export function Autocomplete<T>({
  label,
  options,
  value,
  onChange,
  onBlur,
  getOptionLabel = defaultGetOptionLabel as (option: T) => string,
  isOptionEqualToValue,
  groupBy,
  error,
  disabled,
  placeholder,
  renderOption,
  filterOptions,
}: AutocompleteProps<T>): ReactNode {
  return (
    <>
      <MuiAutocomplete
        size="small"
        options={options}
        value={value ?? null}
        onChange={(_event, newValue) => onChange(newValue)}
        onBlur={onBlur}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={isOptionEqualToValue}
        groupBy={groupBy}
        disabled={disabled}
        renderOption={renderOption}
        filterOptions={filterOptions}
        renderInput={(params) => (
          <TextField {...params} label={label} placeholder={placeholder} error={!!error} size="small" />
        )}
      />
      {error && <FormHelperText error>{error}</FormHelperText>}
    </>
  );
}

// ============= Multi =============

export interface AutocompleteMultiProps<T> extends AutocompleteBaseProps<T> {
  value: T[];
  onChange: (value: T[]) => void;
  renderOption?: MuiAutocompleteProps<T, true, false, false>["renderOption"];
  filterOptions?: MuiAutocompleteProps<T, true, false, false>["filterOptions"];
  minRows?: number;
  disableCloseOnSelect?: boolean;
}

export function AutocompleteMulti<T>({
  label,
  options,
  value,
  onChange,
  onBlur,
  getOptionLabel = defaultGetOptionLabel as (option: T) => string,
  isOptionEqualToValue,
  groupBy,
  error,
  disabled,
  placeholder,
  renderOption,
  filterOptions,
  minRows,
  disableCloseOnSelect = false,
}: AutocompleteMultiProps<T>): ReactNode {
  // Control the input so the typed filter survives selecting an option (MUI clears it by
  // default via reason "reset"). The text is only cleared when the user types/clears it
  // themselves or when the dropdown closes — keeping options filtered while multi-selecting.
  const [inputValue, setInputValue] = useState("");

  return (
    <>
      <MuiAutocomplete
        multiple
        size="small"
        options={options}
        value={value ?? []}
        onChange={(_event, newValue) => onChange(newValue)}
        onBlur={onBlur}
        inputValue={inputValue}
        onInputChange={(_event, newInputValue, reason) => {
          // Only react to the user's own edits: typing ("input") or hitting the clear button
          // ("clear"). MUI otherwise tries to wipe the input after toggling an option
          // ("selectOption"/"removeOption"/"reset"); ignoring those keeps the filter so the
          // list stays narrowed while multi-selecting. Closing the popup resets it via onClose.
          if (reason === "input" || reason === "clear") setInputValue(newInputValue);
        }}
        onClose={() => setInputValue("")}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={isOptionEqualToValue}
        groupBy={groupBy}
        disabled={disabled}
        disableCloseOnSelect={disableCloseOnSelect}
        renderOption={renderOption}
        filterOptions={filterOptions}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            error={!!error}
            size="small"
            sx={
              minRows
                ? {
                    "& .MuiInputBase-root": {
                      minHeight: `${minRows * 2.2}em`,
                      alignItems: "flex-start",
                      flexWrap: "wrap",
                    },
                  }
                : undefined
            }
          />
        )}
      />
      {error && <FormHelperText error>{error}</FormHelperText>}
    </>
  );
}

export default Autocomplete;
